import { useCallback, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import {
  IOptionParentIssuer,
  IGroupParentIssuer,
  IParentPayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IParent';
import { IIssuerPayload } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IIssuer';
import { useArticlesParentsList } from '@/creditflowresearch/application/hooks/apiHooks/useArticlesParentsList';
import { useArticlesIssuersList } from '@/creditflowresearch/application/hooks/apiHooks/useArticlesIssuersList';
import {
  getIssuerOptions,
  getParentIssuerGroupMapper,
  getParentOptions,
} from '@/creditflowresearch/application/ArticlesAndStats/Articles/Filter/helpers';
import { CFR_ARTICLES } from '@/creditflowresearch/ui/components/creditFlow/articles/FiltersButton/constants';
import { initialArticlesFilterState } from '@/creditflowresearch/application/ArticlesAndStats/Articles/constants';
import { useFiltersContext } from '@framework/ui/Organisms/Filters/FiltersContext';

const stateSelector = (filtersState): {
  includedParentIds: string[],
  includedIssuerIds: string[],
} => {
  const filters = isEmpty(filtersState[CFR_ARTICLES]) ? initialArticlesFilterState : filtersState[CFR_ARTICLES];

  return {
    includedParentIds: filters.includedParentIds,
    includedIssuerIds: filters.includedIssuerIds,
  };
};

const useParentIssuerSelect = () => {
  const [selected, setSelected] = useState<IOptionParentIssuer[]>([]);
  const [currentOptions, setCurrentOptions] = useState<IGroupParentIssuer[]>([]);
  const { getParentsList } = useArticlesParentsList();
  const { getIssuersList } = useArticlesIssuersList();
  const { filters } = useFiltersContext();
  const { includedParentIds, includedIssuerIds } = stateSelector(filters);

  const getParentsAndIssuers = async (
    search: string | null = null,
    includedParentIds: string[] = [],
    includedIssuerIds: string[] = [],
    ) => {
    const parentPayload: IParentPayload = { search, filter: { includedParentIds } };
    const issuerPayload: IIssuerPayload = { search, filter: { includedIssuerIds } };

    return Promise.all([
      getParentsList(parentPayload),
      getIssuersList(issuerPayload),
    ]);
  };

  const initParentIssuer = async (includedParentIds: string[], includedIssuerIds: string[]) => {
    if (isEmpty(includedParentIds) && isEmpty(includedIssuerIds)) {
      return;
    }

    const [parents, issuers] = await getParentsAndIssuers(null, includedParentIds, includedIssuerIds);
    const options = getParentIssuerGroupMapper(parents.selected, issuers.selected);

    setCurrentOptions(options);
    setSelected([...getParentOptions(parents.selected), ...getIssuerOptions(issuers.selected)]);
  };

  const loadParentIssuer = useCallback(async (
    search: string,
    callback: (options: IGroupParentIssuer[]) => void,
  ) => {
    const includedParentIds = selected.filter(({ isParent }) => isParent).map(({ id }) => id);
    const includedIssuerIds = selected.filter(({ isParent }) => !isParent).map(({ id }) => id);

    setCurrentOptions([]);

    const [parents, issuers] = await getParentsAndIssuers(search, includedParentIds, includedIssuerIds);

    const options = getParentIssuerGroupMapper(
      [...parents.allowed, ...parents.selected],
      [...issuers.allowed, ...issuers.selected],
    );

    setCurrentOptions(options);
    callback(options);
    return options;
  }, [selected]);

  useEffect(() => {
    if (includedIssuerIds.length === 0 && selected.length > 0) {
      const selectedParent = selected.filter(({ isParent }) => isParent);

      setSelected(selectedParent);
    }
  }, [includedIssuerIds.length]);

  useEffect(() => {
    if (includedParentIds.length === 0 && selected.length > 0) {
      const selectedIssuer = selected.filter(({ isParent }) => !isParent);

      setSelected(selectedIssuer);
    }
  }, [includedParentIds.length]);

  return {
    selected,
    setSelected,
    currentOptions,
    loadParentIssuer,
    initParentIssuer,
  };
};

export default useParentIssuerSelect;
